import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Textarea } from '../../components/ui/FormField.js';
import { api } from '../../services/api.js';
import { rm } from '../penalties/penalties.js';

/**
 * 417 (#1464, US4, mock-up frame 2): the rental's mileage package and, once it ends, the km charge the platform
 * proposed. Staff with permission to record penalties confirm it, adjust the km (a reason required) or dismiss it (a
 * reason required); a not-measured rental is charged by the km staff enter. Confirming records an "Extra kilometres"
 * penalty, which is when the customer is told.
 */
export interface MileagePackage {
  package: 1 | 2;
  price_minor: number;
  allowance_km: number | null;
  rate_minor_per_km: number;
}
export interface MileageCharge {
  status: 'proposed' | 'no_charge' | 'not_measured' | 'confirmed' | 'dismissed';
  package: 1 | 2;
  km_measured: number | null;
  allowance_km: number | null;
  km_charged: number | null;
  rate_minor_per_km: number;
  amount_minor: number | null;
  proposed_at: string;
  decided_at?: string;
  reason?: string;
  penalty_id?: string;
}

const STATUS: Record<MileageCharge['status'], { word: string; tone: string }> = {
  proposed: { word: 'Proposed', tone: 'bg-semantic-warning-bg text-semantic-warning-text' },
  not_measured: { word: 'Not measured', tone: 'bg-semantic-warning-bg text-semantic-warning-text' },
  no_charge: { word: 'No km charge', tone: 'bg-surface-feature text-steel' },
  confirmed: { word: 'Confirmed', tone: 'bg-semantic-success-bg text-semantic-success-text' },
  dismissed: { word: 'Dismissed', tone: 'bg-surface-feature text-steel' },
};

const decidedWhen = new Intl.DateTimeFormat('en-MY', {
  timeZone: 'Asia/Kuala_Lumpur',
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
/** `25 Oct, 9:12 pm`, in Malaysia time. */
function when(iso: string): string {
  const p = Object.fromEntries(
    decidedWhen.formatToParts(new Date(iso)).map((x) => [x.type, x.value]),
  );
  return `${p.day} ${p.month}, ${p.hour}:${p.minute} ${String(p.dayPeriod).toLowerCase()}`;
}
const km1 = (km: number) => km.toFixed(1);
const packageWords = (pkg: MileagePackage) =>
  pkg.package === 1 ? '1 · Stay in the area' : `2 · Go anywhere · ${pkg.allowance_km} km included`;

export function KmChargeCard({
  bookingId,
  pkg,
  charge,
  canDecide,
}: {
  bookingId: string;
  pkg: MileagePackage;
  charge: MileageCharge | null;
  canDecide: boolean;
}) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'none' | 'adjust' | 'dismiss'>('none');
  const [km, setKm] = useState('');
  const [reason, setReason] = useState('');
  const decide = useMutation({
    mutationFn: (v: { action: 'confirm' | 'dismiss'; body: Record<string, unknown> }) =>
      api.post(`/bookings/${bookingId}/mileage-charge/${v.action}`, v.body),
    onSuccess: () => {
      setMode('none');
      void queryClient.invalidateQueries({ queryKey: ['booking'] });
      void queryClient.invalidateQueries({ queryKey: ['penalties'] });
    },
  });

  const rate = pkg.rate_minor_per_km;
  const typedKm = Number(km);
  const kmOk =
    km.trim() !== '' && typedKm > 0 && Math.abs(typedKm * 10 - Math.round(typedKm * 10)) < 1e-9;
  const adjustedMinor = kmOk ? Math.round(typedKm * rate) : null;
  const open = charge && (charge.status === 'proposed' || charge.status === 'not_measured');
  const status = charge ? STATUS[charge.status] : null;

  let outcome: string | null = null;
  if (charge?.status === 'no_charge') {
    outcome =
      charge.package === 1
        ? `Package 1 · ${km1(charge.km_measured ?? 0)} km outside the area.`
        : `Package 2 · ${km1(charge.km_measured ?? 0)} of ${charge.allowance_km} km used.`;
  } else if (charge?.status === 'confirmed') {
    // What the readings would have charged: the km outside (Package 1) or over the allowance (Package 2). Shown only
    // when staff charged something else.
    const fromReadings =
      charge.km_measured === null
        ? null
        : charge.package === 2
          ? Math.max(0, charge.km_measured - (charge.allowance_km ?? 0))
          : charge.km_measured;
    const measured =
      fromReadings !== null && Math.abs(fromReadings - (charge.km_charged ?? 0)) >= 0.05
        ? ` (measured ${km1(fromReadings)} km)`
        : '';
    outcome = [
      `${rm(charge.amount_minor ?? 0)} for ${charge.km_charged} km${measured}`,
      charge.decided_at ? when(charge.decided_at) : null,
      charge.reason ? `“${charge.reason}”` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  } else if (charge?.status === 'dismissed') {
    outcome = [
      charge.amount_minor ? `${rm(charge.amount_minor)} not charged` : 'Not charged',
      charge.decided_at ? when(charge.decided_at) : null,
      charge.reason ? `“${charge.reason}”` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  return (
    <Card data-testid="km-charge-card" data-jump="Km charge">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-steel">Km charge</h2>
        {status && (
          <span
            data-testid="km-charge-status"
            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${status.tone}`}
          >
            {status.word}
          </span>
        )}
      </div>
      <dl className="grid grid-cols-[150px_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-steel">Package</dt>
        <dd data-testid="km-charge-package">{packageWords(pkg)}</dd>
        {charge?.status === 'proposed' && (
          <>
            <dt className="text-steel">{charge.package === 1 ? 'Km outside' : 'Km driven'}</dt>
            <dd data-testid="km-charge-measured">{km1(charge.km_measured ?? 0)} km</dd>
            {charge.package === 2 && (
              <>
                <dt className="text-steel">Over the allowance</dt>
                <dd data-testid="km-charge-over">
                  {km1(charge.km_charged ?? 0)} km × {rm(rate)}
                </dd>
              </>
            )}
            <dt className="text-steel">Proposed charge</dt>
            <dd data-testid="km-charge-amount" className="font-semibold">
              {rm(charge.amount_minor ?? 0)}
            </dd>
          </>
        )}
      </dl>
      {!charge && (
        <p className="mt-2 text-sm text-steel">The km charge is proposed when the rental ends.</p>
      )}
      {charge?.status === 'not_measured' && (
        <p className="mt-2 text-sm text-steel">
          This rental was not measured to the end. Enter the km to charge, or dismiss.
        </p>
      )}
      {outcome && (
        <p data-testid="km-charge-outcome" className="mt-2 text-sm text-ink">
          {outcome}
        </p>
      )}
      {charge?.status === 'confirmed' && charge.penalty_id && (
        <a href="#booking-penalties" className="mt-1 inline-block text-sm text-primary underline">
          Penalty →
        </a>
      )}

      {open && !canDecide && (
        <p className="mt-3 text-xs text-steel">Deciding needs permission to record penalties.</p>
      )}
      {open && canDecide && (
        <div className="mt-4 flex flex-col gap-3 border-t border-hairline pt-4">
          {mode === 'none' && (
            <>
              <div className="flex flex-wrap gap-2">
                {charge.status === 'proposed' && (
                  <Button
                    loading={decide.isPending}
                    onClick={() => decide.mutate({ action: 'confirm', body: {} })}
                  >
                    {`Confirm ${rm(charge.amount_minor ?? 0)}`}
                  </Button>
                )}
                <Button variant="secondary" onClick={() => setMode('adjust')}>
                  {charge.status === 'proposed' ? 'Adjust km…' : 'Enter km…'}
                </Button>
                <Button variant="secondary" onClick={() => setMode('dismiss')}>
                  Dismiss…
                </Button>
              </div>
              <p className="text-xs text-steel">
                Confirming records an "Extra kilometres" penalty (CARii keeps its penalty share; the
                rest goes to the supplier). The customer is told only then.
              </p>
            </>
          )}
          {mode === 'adjust' && (
            <div className="flex flex-col gap-3">
              <FormField label="Km to charge" name="km-charge-km">
                <Input
                  id="km-charge-km"
                  type="number"
                  min={0.1}
                  step="0.1"
                  value={km}
                  onChange={(e) => setKm(e.target.value)}
                />
              </FormField>
              {adjustedMinor !== null && (
                <p data-testid="km-charge-adjusted" className="text-xs text-steel">
                  = {rm(adjustedMinor)} at {rm(rate)}/km
                </p>
              )}
              <FormField
                label="Reason"
                name="km-charge-reason"
                hint="Required. Kept with the decision."
              >
                <Textarea
                  id="km-charge-reason"
                  maxLength={150}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </FormField>
              <div className="flex gap-2">
                <Button
                  disabled={adjustedMinor === null || adjustedMinor < 1 || !reason.trim()}
                  loading={decide.isPending}
                  onClick={() =>
                    decide.mutate({
                      action: 'confirm',
                      body: { km: typedKm, reason: reason.trim() },
                    })
                  }
                >
                  {`Confirm ${rm(adjustedMinor ?? 0)}`}
                </Button>
                <Button variant="secondary" onClick={() => setMode('none')}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {mode === 'dismiss' && (
            <div className="flex flex-col gap-3">
              <FormField
                label="Reason"
                name="km-charge-dismiss"
                hint="Required. Kept with the decision."
              >
                <Textarea
                  id="km-charge-dismiss"
                  maxLength={150}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </FormField>
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  disabled={!reason.trim()}
                  loading={decide.isPending}
                  onClick={() =>
                    decide.mutate({ action: 'dismiss', body: { reason: reason.trim() } })
                  }
                >
                  Dismiss charge
                </Button>
                <Button variant="secondary" onClick={() => setMode('none')}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {decide.error && (
            <p role="alert" className="text-sm text-semantic-danger-text">
              {(decide.error as { message?: string }).message ?? 'The decision could not be saved.'}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
